import { createContext, useContext, useState, ReactNode, Dispatch, SetStateAction } from "react";
import { MRT_PaginationState, MRT_SortingState } from "material-react-table";
import { useGetAllOrganization, CompanyGrid } from "src/api/client-api/company/useGetAllOrganization";
import { useGetSuppliers } from "src/api/client-api/company/useGetSuppliers";
import { useDelayedSearch } from "src/utils/useDelayedSearch";
import { useGridColumns, GridColumnsType } from "src/components/Grid/hooks/columnsExtension/useGridColumns";
import { useCompanyColumns } from "./useCompanyColumns";

type CompanyListContextInitialValues = {
    companyPage: MRT_PaginationState;
    setCompanyPage: Dispatch<SetStateAction<MRT_PaginationState>>;

    sorting: MRT_SortingState;
    setSorting: Dispatch<SetStateAction<MRT_SortingState>>;

    totalItems: number | undefined;

    companies: CompanyGrid[] | undefined;
    mrt_columns: GridColumnsType<CompanyGrid>;
    isLoading: boolean;

    searchCompany: string;
    delayedSearchCompany: string;
    setSearchCompany: (arg1: string) => void;
    resetSearch: () => void;

    // Modal states
    showCreateModal: boolean;
    setShowCreateModal: Dispatch<SetStateAction<boolean>>;

    gridKey: string;
    isProvider?: boolean;
};

const CompanyListContext = createContext<CompanyListContextInitialValues | undefined>(undefined);

type CompanyListContextProviderProps = {
    children: ReactNode;
    isProvider?: boolean;
};

export const CompanyListContextProvider = (props: CompanyListContextProviderProps) => {
    const { children, isProvider } = props;

    const gridKey = isProvider ? "settings-providers-grid" : "settings-companies-grid";

    const [companyPage, setCompanyPage] = useState<MRT_PaginationState>({
        pageIndex: 0,
        pageSize: 20,
    });
    const [sorting, setSorting] = useState<MRT_SortingState>([{ id: "name", desc: false }]);

    // Modal states
    const [showCreateModal, setShowCreateModal] = useState(false);

    const search = useDelayedSearch();
    const isSupplierList = isProvider === true;
    const organizationList = useGetAllOrganization({
        cacheTime: 0,
        page: companyPage.pageIndex + 1,
        itemsPerPage: companyPage.pageSize,
        search: search.delayedSearch,
        isProvider: isProvider,
        sorting: sorting,
        enabled: !isSupplierList,
    });
    const supplierList = useGetSuppliers({
        page: companyPage.pageIndex + 1,
        itemsPerPage: companyPage.pageSize,
        search: search.delayedSearch,
        enabled: isSupplierList,
    });
    const companies = isSupplierList ? supplierList.organizations : organizationList.companies;
    const isLoading = isSupplierList ? supplierList.isLoading : organizationList.isLoading;
    const totalItems = isSupplierList ? supplierList.totalItems : organizationList.totalItems;

    const mrt_companyColumns = useCompanyColumns(companies, isProvider);
    const mrt_columns = useGridColumns(mrt_companyColumns, gridKey);

    const items: CompanyListContextInitialValues = {
        mrt_columns,

        companyPage,
        setCompanyPage,
        totalItems,

        sorting,
        setSorting,

        companies,
        isLoading,

        searchCompany: search.search,
        delayedSearchCompany: search.delayedSearch,
        setSearchCompany: search.setSearch,
        resetSearch: search.resetSearch,

        // Modal states
        showCreateModal,
        setShowCreateModal,

        gridKey: gridKey,
        isProvider,
    };

    return <CompanyListContext.Provider value={items}>{children}</CompanyListContext.Provider>;
};

export const useCompanyListContext = (): CompanyListContextInitialValues => {
    const context = useContext(CompanyListContext);
    if (context === undefined) {
        throw new Error("useCompanyListContext must be used within a CompanyListContextProvider");
    }
    return context;
};
